import { useEffect, useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Snackbar,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { sanitizeHTML } from 'utils/helpers';

import {
  getMessageTemplates,
  getPartnerPreferences,
  postPartnerPreferences,
  SuggestedMessagesTemplates,
  TriggerCodes,
} from 'services/suggestedMessages';

import { TriggersI18nKeys } from 'components/MessageTipsDrawer/constants';
import * as S from 'components/MessageTipsDrawer/styles';

import { ToneStepProps } from '../types';

export const ToneStep: React.FC<ToneStepProps> = ({
  onClose,
  triggerCode,
  onPreviousStep,
}) => {
  const intl = useIntl();
  const [canSubmit, setCanSubmit] = useState(true);
  const [selectedTemplate, setSelectedTemplate] =
    useState<SuggestedMessagesTemplates>();

  const handleCloseDrawer = () => {
    if (onClose) onClose();
  };

  const {
    data: suggestedMessagesTemplates,
    isLoading: isGetMessageTemplatesLoading,
    isError: isGetMessageTemplatesError,
  } = useQuery({
    queryFn: () => getMessageTemplates(triggerCode as TriggerCodes),
    queryKey: ['messageTemplates', triggerCode],
  });

  const { data: partnerPreferences } = useQuery({
    queryFn: () => getPartnerPreferences('first_booking'),
    queryKey: ['partnerPreferences'],
  });

  const {
    mutate,
    isError,
    isSuccess,
    isLoading: isSubmittingPartnerPreferences,
  } = useMutation({
    mutationFn: ({ messageTemplateId }: { messageTemplateId: number }) =>
      postPartnerPreferences(messageTemplateId),
    onError(error) {
      datadogLogs.logger.error('Error on update message template id', {
        error,
      });
    },
  });

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, isDirty },
    reset,
  } = useForm({
    defaultValues: {
      messageTemplateId: selectedTemplate?.id,
    },
  });

  const handlePreviousStep = () => {
    if (onPreviousStep) onPreviousStep();
  };

  const handleSelectMessageTemplate = (event: SelectChangeEvent) => {
    const id = String(event.target.value);

    const template = suggestedMessagesTemplates?.find(
      eachTemplate => String(eachTemplate.id) === id,
    );

    setSelectedTemplate(template);
    setCanSubmit(true);
  };

  const handleUpdateMessageTemplate = ({
    messageTemplateId,
  }: {
    messageTemplateId?: number;
  }) => {
    mutate({ messageTemplateId: messageTemplateId as number });
    reset();
    setCanSubmit(false);
  };

  const theme = TriggersI18nKeys[triggerCode as TriggerCodes];
  const isDifferentFromTheCurrent =
    selectedTemplate?.id !== partnerPreferences?.[0].message_templates[0].id;
  const isLoading =
    isGetMessageTemplatesLoading ||
    isSubmitting ||
    isSubmittingPartnerPreferences;

  useEffect(() => {
    setSelectedTemplate(
      partnerPreferences?.[0].message_templates[0] ||
        suggestedMessagesTemplates?.[0],
    );
  }, [partnerPreferences, suggestedMessagesTemplates]);

  return (
    <S.Wrapper>
      <S.Header>
        <IconButton onClick={handlePreviousStep}>
          <S.StepBackIcon />
        </IconButton>
        <Typography variant="overline" letterSpacing={1}>
          {intl.formatMessage({ id: theme.title })}
        </Typography>
        <IconButton onClick={handleCloseDrawer}>
          <S.CloseDrawerIcon />
        </IconButton>
      </S.Header>

      <FormControl
        fullWidth
        sx={{ my: 2 }}
        component="form"
        onSubmit={handleSubmit(handleUpdateMessageTemplate)}
      >
        {!isGetMessageTemplatesLoading && (
          <Select
            value={String(
              selectedTemplate?.id ?? suggestedMessagesTemplates?.[0].id,
            )}
            {...register('messageTemplateId')}
            onChange={event => {
              handleSelectMessageTemplate(event);
              register('messageTemplateId').onChange(event);
            }}
          >
            {suggestedMessagesTemplates?.map(template => (
              <MenuItem key={template.tags.tone_of_voice} value={template.id}>
                {template.tags.tone_of_voice}
              </MenuItem>
            ))}
          </Select>
        )}

        {isGetMessageTemplatesLoading && (
          <>
            <>
              <Skeleton
                variant="rounded"
                width="100%"
                height={56}
                animation="wave"
              />

              <Skeleton
                variant="rounded"
                width="100%"
                height={180}
                animation="wave"
                sx={{ mt: 2 }}
              />
            </>
          </>
        )}

        {!isGetMessageTemplatesLoading && selectedTemplate && (
          <S.MessagePreviewContainer data-testid="messagePreviewContainer">
            <S.MessagePreviewText
              dangerouslySetInnerHTML={sanitizeHTML(selectedTemplate.message, {
                ALLOWED_TAGS: ['b'],
                ALLOWED_ATTR: [],
              })}
            />
          </S.MessagePreviewContainer>
        )}

        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          {isDirty && (
            <Button size="small" onClick={handlePreviousStep}>
              {intl.formatMessage({
                id: 'MESSAGETIPS.EDITSCHEDULE.CANCELBUTTON',
              })}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            type="submit"
            disabled={isLoading || !canSubmit || !isDifferentFromTheCurrent}
          >
            {intl.formatMessage({ id: 'MESSAGETIPS.EDITSCHEDULE.SAVEBUTTON' })}
          </Button>
        </Box>
      </FormControl>

      {isSuccess && (
        <S.FeedbackSuccess severity="success" variant="filled">
          <Typography variant="body2" color="text.primary">
            {intl.formatMessage({ id: 'MESSAGETIPS.GLOBAL.UPDATEDTOALL' })}
          </Typography>
        </S.FeedbackSuccess>
      )}

      {isError && (
        <S.FeedbackError severity="error" variant="filled">
          <Typography variant="body2">
            {intl.formatMessage({ id: 'MESSAGETIPS.GLOBALERROR.SYSTEMERROR' })}
          </Typography>
        </S.FeedbackError>
      )}

      <Snackbar open={isGetMessageTemplatesError} autoHideDuration={6000}>
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          {intl.formatMessage({ id: 'MESSAGETIPS.GLOBALERROR.SYSTEMERROR' })}
        </Alert>
      </Snackbar>
    </S.Wrapper>
  );
};
